import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Camera, Images, ImagePlus, Trash2, FileText } from "lucide-react";
import ProductImage from "../components/ProductImage";

const empty = {
  name: "",
  feature: "",
  stock: 0,
  min_stock: 3,
  cost_price: "",
  sale_price: "",
  description: "",
  category: "Diğer",
  image_path: "",
};

export default function NewProduct() {
  const nav = useNavigate();
  const [form, setForm] = useState(empty);
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [nextSku, setNextSku] = useState("");
  const [previewLocal, setPreviewLocal] = useState(""); // temporary object URL for preview before upload finishes
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
    api
      .get("/products/next-sku")
      .then((r) => setNextSku(r.data.next_sku))
      .catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFile = async (file) => {
    if (!file) return;
    // Instant local preview
    const localUrl = URL.createObjectURL(file);
    setPreviewLocal(localUrl);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("image_path", data.path);
      toast.success("Fotoğraf yüklendi");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Yükleme başarısız");
      setPreviewLocal("");
    } finally {
      setUploading(false);
    }
  };

  const onCamera = (e) => handleFile(e.target.files?.[0]);
  const onGallery = (e) => handleFile(e.target.files?.[0]);

  const removePhoto = () => {
    setPreviewLocal("");
    set("image_path", "");
    if (cameraRef.current) cameraRef.current.value = "";
    if (galleryRef.current) galleryRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Ürün adı zorunludur");
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        feature: form.feature,
        category: form.category,
        description: form.description,
        stock: Number(form.stock) || 0,
        min_stock: Number(form.min_stock) || 0,
        cost_price: Number(form.cost_price) || 0,
        sale_price: Number(form.sale_price) || 0,
        image_path: form.image_path || "",
        // sku & barcode are auto-generated server-side
      };
      const { data } = await api.post("/products", payload);
      toast.success(`Ürün eklendi (${data.sku})`);
      nav("/urunler");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kayıt başarısız");
    } finally {
      setSaving(false);
    }
  };

  const margin =
    form.cost_price > 0 && form.sale_price > 0
      ? (((Number(form.sale_price) - Number(form.cost_price)) /
          Number(form.cost_price)) *
          100).toFixed(1)
      : null;

  const hasPhoto = !!form.image_path || !!previewLocal;

  return (
    <div data-testid="new-product-root" className="max-w-5xl">
      <div className="mb-6">
        <div className="font-mono-code text-[10px] tracking-[0.3em] text-stone-500">
          ENVANTER GİRİŞİ
        </div>
        <h1 className="font-serif-display text-3xl sm:text-4xl text-stone-900">
          Yeni Ürün Ekle
        </h1>
        <p className="text-sm text-stone-500 mt-1">
          Fotoğrafı ekleyin, temel bilgileri girin — kod ve barkod otomatik
          oluşturulur.
        </p>
      </div>

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Photo panel */}
        <div className="card-lux p-6 lg:col-span-1">
          <div className="text-xs uppercase tracking-widest font-semibold text-stone-500 mb-3">
            Ürün Fotoğrafı
          </div>

          <div className="flex flex-col items-center gap-4">
            {form.image_path ? (
              <ProductImage
                path={form.image_path}
                size={220}
                className="w-[220px] h-[220px]"
                alt="preview"
              />
            ) : previewLocal ? (
              <img
                src={previewLocal}
                alt="preview"
                className="w-[220px] h-[220px] object-cover rounded-md border border-stone-200"
              />
            ) : (
              <div className="w-[220px] h-[220px] rounded-xl border-2 border-dashed border-stone-300 flex flex-col items-center justify-center bg-stone-50 text-stone-400 text-xs">
                <ImagePlus className="w-8 h-8 mb-1" />
                Henüz fotoğraf yok
              </div>
            )}

            {/* Hidden inputs */}
            <input
              ref={cameraRef}
              data-testid="upload-camera-input"
              type="file"
              accept="image/*"
              capture="environment"
              onChange={onCamera}
              className="hidden"
              id="prod-photo-camera"
            />
            <input
              ref={galleryRef}
              data-testid="upload-gallery-input"
              type="file"
              accept="image/*"
              onChange={onGallery}
              className="hidden"
              id="prod-photo-gallery"
            />

            <div className="grid grid-cols-2 gap-2 w-full">
              <label
                htmlFor="prod-photo-camera"
                data-testid="btn-take-photo"
                className="cursor-pointer inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg gold-gradient text-white text-sm active:scale-[0.98] transition"
              >
                <Camera className="w-4 h-4" />
                Fotoğraf Çek
              </label>
              <label
                htmlFor="prod-photo-gallery"
                data-testid="btn-pick-gallery"
                className="cursor-pointer inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-stone-200 hover:bg-stone-50 text-sm"
              >
                <Images className="w-4 h-4" />
                Galeriden Seç
              </label>
            </div>

            {hasPhoto && (
              <button
                type="button"
                data-testid="btn-remove-photo"
                onClick={removePhoto}
                className="inline-flex items-center gap-2 text-xs text-red-700 hover:text-red-900"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Fotoğrafı Kaldır
              </button>
            )}

            {uploading && (
              <div className="text-xs text-amber-700">Optimize ediliyor…</div>
            )}
            <div className="text-[11px] text-stone-400 text-center">
              Yüklenen görseller otomatik olarak yeniden boyutlandırılıp
              sıkıştırılır.
            </div>
          </div>

          {/* Auto SKU / Barcode read-only preview */}
          <div className="mt-6 pt-6 border-t border-stone-200 space-y-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest font-semibold text-stone-500 mb-1">
                Ürün Kodu (Otomatik)
              </div>
              <div
                data-testid="np-sku-preview"
                className="font-mono-code text-sm px-3 py-2 rounded-lg bg-stone-50 border border-stone-200 text-stone-700"
              >
                {nextSku || "kayıt sırasında oluşturulacak"}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest font-semibold text-stone-500 mb-1">
                Barkod (Otomatik)
              </div>
              <div className="font-mono-code text-sm px-3 py-2 rounded-lg bg-stone-50 border border-stone-200 text-stone-700">
                Kayıt sırasında oluşturulacak
              </div>
            </div>
          </div>
        </div>

        {/* Fields panel — order per spec */}
        <div className="card-lux p-6 lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Field label="Ürün Adı *" testid="np-name">
              <input
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                className="input"
                placeholder="Örn: Zirkon Taşlı Altın Kaplama Kolye"
                required
                autoFocus
              />
            </Field>
          </div>
          <Field label="Ürün Özelliği" testid="np-feature">
            <input
              value={form.feature}
              onChange={(e) => set("feature", e.target.value)}
              className="input"
              placeholder="Örn: 14K Kaplama, Zirkon Taş"
            />
          </Field>
          <Field label="Kategori" testid="np-category">
            <select
              value={form.category}
              onChange={(e) => set("category", e.target.value)}
              className="input bg-white"
            >
              {categories.map((c) => (
                <option key={c.id} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Stok Adedi" testid="np-stock">
            <input
              type="number"
              inputMode="numeric"
              value={form.stock}
              onChange={(e) => set("stock", e.target.value)}
              className="input"
              min={0}
            />
          </Field>
          <Field label="Min. Stok Seviyesi" testid="np-min-stock">
            <input
              type="number"
              inputMode="numeric"
              value={form.min_stock}
              onChange={(e) => set("min_stock", e.target.value)}
              className="input"
              min={0}
            />
          </Field>

          <Field
            label={`Alış Fiyatı (₺)${margin ? ` — Kâr: %${margin}` : ""}`}
            testid="np-cost"
          >
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              value={form.cost_price}
              onChange={(e) => set("cost_price", e.target.value)}
              className="input"
              min={0}
              placeholder="0,00"
            />
          </Field>
          <Field label="Satış Fiyatı (₺)" testid="np-sale">
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              value={form.sale_price}
              onChange={(e) => set("sale_price", e.target.value)}
              className="input"
              min={0}
              placeholder="0,00"
            />
          </Field>

          <div className="sm:col-span-2">
            <Field label="Açıklama" testid="np-description">
              <textarea
                rows={3}
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
                className="input"
                placeholder="Ürün hakkında notlar..."
              />
            </Field>
          </div>

          <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => {
                setForm(empty);
                setPreviewLocal("");
              }}
              className="px-4 py-2 rounded-lg border border-stone-200 text-sm hover:bg-stone-50"
            >
              Temizle
            </button>
            <button
              data-testid="np-submit"
              type="submit"
              disabled={saving || uploading}
              className="px-5 py-2 rounded-lg gold-gradient text-white text-sm disabled:opacity-60"
            >
              {saving ? "Kaydediliyor..." : "Ürünü Kaydet"}
            </button>
          </div>
        </div>
      </form>

      <style>{`
        .input {
          width: 100%;
          padding: 0.6rem 0.75rem;
          border-radius: 0.5rem;
          border: 1px solid #e7e5e4;
          outline: none;
          transition: border-color 0.15s;
          background: white;
          font-size: 0.95rem;
        }
        .input:focus { border-color: #d4af37; box-shadow: 0 0 0 2px rgba(212,175,55,0.15); }
      `}</style>
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <div className="text-xs uppercase tracking-wider font-semibold text-stone-500 mb-1">
        {label}
      </div>
      {children}
    </label>
  );
}
